import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ClipboardList, Award, ArrowRight, MessageSquare, Mail, AlertTriangle, Upload, FileText, CheckCircle, Info, Shield } from 'lucide-react';
import { useApi } from './DashboardLayout';

const PRIO_COLORS = { hoch: '#EF4444', mittel: '#F59E0B', niedrig: '#10B981' };
const STATUS_LABELS = { offen: 'Offen', in_arbeit: 'In Arbeit', erledigt: 'Erledigt' };

const BANNER_STYLES = {
  info:    { color: '#3B82F6', icon: Info,           title: 'Hinweis' },
  warning: { color: '#F59E0B', icon: AlertTriangle,  title: 'Achtung' },
  success: { color: '#10B981', icon: CheckCircle,    title: 'Mitteilung' },
};

export function DashboardBanner({ banner }) {
  if (!banner?.content) return null;
  const style = BANNER_STYLES[banner.banner_type] || BANNER_STYLES.info;
  const Icon = style.icon;
  return (
    <div className="card" data-testid="info-banner-card" style={{ borderLeft: `4px solid ${style.color}`, background: 'var(--bg-card)' }}>
      <div className="card-header">
        <h3>
          <span className="card-header-icon" style={{ background: `${style.color}1A`, color: style.color }}>
            <Icon size={18} />
          </span>
          {style.title}
        </h3>
      </div>
      <div className="card-body">
        <div style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--text)', margin: 0, whiteSpace: 'pre-line' }}
          dangerouslySetInnerHTML={{ __html: formatBannerText(banner.content) }} />
        {banner.show_ticket_link && (
          <Link to="/app/tickets" data-testid="info-banner-ticket-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 16, padding: '8px 16px',
              background: style.color, color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none',
            }}>
            <MessageSquare size={14} /> Ticket erstellen
          </Link>
        )}
      </div>
    </div>
  );
}

function formatBannerText(text) {
  if (!text) return '';
  // If content already contains HTML tags (from WYSIWYG), sanitize and return
  if (/<[a-z][\s\S]*>/i.test(text)) {
    // Strip all tags except b, strong, i, em, br, p
    return text.replace(/<(?!\/?(?:b|strong|i|em|br|p)\b)[^>]*>/gi, '');
  }
  // Legacy markdown fallback
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

function StatCard({ label, value, icon: Icon, color, link }) {
  return (
    <div className="stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="stat-card-top">
        <div>
          <div className="stat-card-label">{label}</div>
          <div className="stat-card-value">{value}</div>
        </div>
        <div className={`stat-card-icon ${color}`}><Icon size={22} /></div>
      </div>
      {link && <Link to={link} className="stat-card-link">Anzeigen <ArrowRight size={14} /></Link>}
    </div>
  );
}

function TicketList({ tickets }) {
  if (!tickets?.length) return (
    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
      <MessageSquare size={28} style={{ color: '#d1d5db', marginBottom: 8 }} />
      <p>Keine Tickets vorhanden</p>
    </div>
  );
  return (
    <table style={{ width: '100%', fontSize: 13 }}>
      <tbody>
        {tickets.map((t, i) => (
          <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
            <td style={{ padding: '10px 16px' }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>#{t.id} {t.title}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{t.created_by_email}</div>
            </td>
            <td style={{ padding: '10px 8px', textAlign: 'right' }}>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12, color: PRIO_COLORS[t.priority] || '#6b7280', background: 'rgba(0,0,0,0.04)' }}>
                {(t.priority || '').toUpperCase()}
              </span>
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 12, marginLeft: 4, background: '#f3f4f6', color: '#374151' }}>
                {STATUS_LABELS[t.status] || t.status}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MailList({ mails, emptyText }) {
  if (!mails?.length) return (
    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
      <Mail size={28} style={{ color: '#d1d5db', marginBottom: 8 }} />
      <p>{emptyText || 'Keine E-Mails vorhanden'}</p>
    </div>
  );
  return (
    <table style={{ width: '100%', fontSize: 13 }}>
      <tbody>
        {mails.map((m, i) => (
          <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
            <td style={{ padding: '10px 16px' }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{m.subject?.substring(0, 45)}{m.subject?.length > 45 ? '...' : ''}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>An: {m.recipient_name || m.recipient_email}</div>
            </td>
            <td style={{ padding: '10px 8px', textAlign: 'right', whiteSpace: 'nowrap', fontSize: 11, color: 'var(--text-muted)' }}>
              {m.sent_at ? new Date(m.sent_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PendingUploads({ uploads, apiFetch, onReviewed }) {
  if (!uploads?.length) return (
    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
      <Upload size={28} style={{ color: '#d1d5db', marginBottom: 8 }} />
      <p>Keine ungeprüften Uploads</p>
    </div>
  );

  const markReviewed = async (upload) => {
    if (!upload._id) return;
    await apiFetch(`/api/downloads/provider-uploads/${upload._id}/review`, { method: 'POST' });
    onReviewed?.();
  };

  return (
    <table style={{ width: '100%', fontSize: 13 }}>
      <tbody>
        {uploads.map((u, i) => (
          <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
            <td style={{ padding: '10px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FileText size={14} style={{ color: '#EF4444', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{u.filename}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                    {u.provider_name} &middot; {u.uploaded_at ? new Date(u.uploaded_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              </div>
            </td>
            <td style={{ padding: '10px 8px', textAlign: 'right' }}>
              <button onClick={() => markReviewed(u)} title="Als geprüft markieren"
                data-testid={`review-upload-${i}`}
                style={{ background: 'none', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer', padding: '4px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, color: '#10B981' }}>
                <CheckCircle size={12} /> Geprüft
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


export default function Dashboard({ userType }) {
  const [stats, setStats] = useState(null);
  const [banners, setBanners] = useState([]);
  const { apiFetch } = useApi();

  useEffect(() => {
    apiFetch('/api/dashboard/stats').then(r => r?.json()).then(d => d && setStats(d));
    apiFetch('/api/settings/info-banner').then(r => r?.json()).then(d => {
      if (d?.banners) setBanners(d.banners.filter(b => b.content));
    }).catch(() => {});
  }, []);

  if (!stats) return <div className="empty-state"><p>Laden...</p></div>;

  // Provider dashboard
  if (stats.user_type === 3) {
    const activeBanners = banners.filter(b => b.enabled_admin && b.content);
    return (
      <div data-testid="dashboard-page">
        <div className="page-header">
          <h2>Dashboard</h2>
          <p>Willkommen in Ihrem Verwaltungsbereich</p>
        </div>

        <div className="stat-grid" data-testid="stat-grid">
          <StatCard label="Meine Angebote" value={stats.my_offers} icon={ClipboardList} color="green" link="/app/offers" />
          <StatCard label="Davon zertifiziert" value={stats.my_certified} icon={Award} color="yellow" />
          {stats.open_tickets > 0 && (
            <StatCard label="Offene Tickets" value={stats.open_tickets} icon={MessageSquare} color="blue" link="/app/tickets" />
          )}
        </div>

        {activeBanners.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: activeBanners.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }} data-testid="dashboard-banners">
            {activeBanners.map(b => <DashboardBanner key={b.id} banner={b} />)}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="card">
            <div className="card-header">
              <h3><span className="card-header-icon blue"><MessageSquare size={18} /></span> Meine Tickets</h3>
              {stats.recent_tickets?.length > 0 && <Link to="/app/tickets" className="card-link">Alle <ArrowRight size={14} /></Link>}
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <TicketList tickets={stats.recent_tickets} />
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3><span className="card-header-icon green"><Mail size={18} /></span> Erhaltene E-Mails</h3>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <MailList mails={stats.recent_mails} emptyText="Keine E-Mails erhalten" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin dashboard
  const adminBanners = banners.filter(b => b.enabled_admin && b.content);
  return (
    <div data-testid="dashboard-page">
      <div className="page-header">
        <h2>Dashboard</h2>
        <p>Willkommen im BGPR Verwaltungssystem</p>
      </div>

      <div className="stat-grid" data-testid="stat-grid">
        {userType === 1 && (
          <StatCard label="Admins" value={stats.admins + stats.superadmins} icon={Users} color="blue" link="/app/admins" />
        )}
        <StatCard label="Anbieter" value={stats.providers} icon={Users} color="green" link="/app/providers" />
        <StatCard label="Zertifiziert" value={stats.certified_offers} icon={Award} color="yellow" link="/app/offers/certified" />
        <StatCard label="Nicht zertifiziert" value={stats.non_certified_offers} icon={ClipboardList} color="red" link="/app/offers/uncertified" />
      </div>

      {adminBanners.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: adminBanners.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }} data-testid="dashboard-banners">
          {adminBanners.map(b => <DashboardBanner key={b.id} banner={b} />)}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card">
          <div className="card-header">
            <h3><span className="card-header-icon blue"><MessageSquare size={18} /></span> Aktuelle Tickets</h3>
            {stats.recent_tickets?.length > 0 && <Link to="/app/tickets" className="card-link">Alle <ArrowRight size={14} /></Link>}
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <TicketList tickets={stats.recent_tickets} />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3><span className="card-header-icon green"><Mail size={18} /></span> Letzte E-Mails</h3>
            {stats.recent_mails?.length > 0 && <Link to="/app/mail/log" className="card-link">Alle <ArrowRight size={14} /></Link>}
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <MailList mails={stats.recent_mails} emptyText="Noch keine E-Mails gesendet" />
          </div>
        </div>
      </div>

      {stats.expiring_certifications?.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3><span className="card-header-icon yellow"><AlertTriangle size={18} /></span> Ablaufende Zertifizierungen (90 Tage)</h3>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <table style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr><th style={{ padding: '8px 16px' }}>Anbieter</th><th>Angebot</th><th style={{ width: 120 }}>Gültig bis</th></tr>
              </thead>
              <tbody>
                {stats.expiring_certifications.map((c, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 16px', fontWeight: 600 }}>{c.provider_name}</td>
                    <td>{c.offer_name || '-'}</td>
                    <td style={{ color: '#DC2626', fontWeight: 600 }}>{c.cert_valid_until?.substring(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* GBM Pending Applications */}
      {stats.gbm_pending > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="gbm-pending-card">
          <div className="card-header">
            <h3>
              <span className="card-header-icon" style={{ background: '#EFF6FF', color: '#2563EB' }}><Shield size={18} /></span>
              Neue GBM-Anträge
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, background: '#2563EB', color: '#fff', padding: '2px 8px', borderRadius: 12 }}>
                {stats.gbm_pending}
              </span>
            </h3>
            <Link to="/app/gbm" className="card-link">Zur GBM-Verwaltung <ArrowRight size={14} /></Link>
          </div>
          <div className="card-body">
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              {stats.gbm_pending === 1
                ? 'Es liegt 1 neuer GBM-Antrag zur Prüfung vor.'
                : `Es liegen ${stats.gbm_pending} neue GBM-Anträge zur Prüfung vor.`}
            </p>
          </div>
        </div>
      )}

      {(stats.pending_uploads_count > 0 || stats.pending_uploads?.length > 0) && (
        <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="pending-uploads-card">
          <div className="card-header">
            <h3>
              <span className="card-header-icon red"><Upload size={18} /></span>
              Neue Anbieter-Uploads
              {stats.pending_uploads_count > 0 && (
                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, background: '#EF4444', color: '#fff', padding: '2px 8px', borderRadius: 12 }}>
                  {stats.pending_uploads_count}
                </span>
              )}
            </h3>
            <Link to="/app/provider-documents-overview" className="card-link">Alle Ordner <ArrowRight size={14} /></Link>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <PendingUploads uploads={stats.pending_uploads} apiFetch={apiFetch}
              onReviewed={() => apiFetch('/api/dashboard/stats').then(r => r?.json()).then(d => d && setStats(d))} />
          </div>
        </div>
      )}
    </div>
  );
}
